import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api-client";
import { formatStep } from "@/lib/formatters";
import type { PipelineStepDTO } from "./useJobTypes";

export function usePipelineSteps(jobTypeId: string | undefined) {
  const [steps, setSteps] = useState<PipelineStepDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!jobTypeId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.get<PipelineStepDTO[]>(
        `/job-types/${jobTypeId}/pipeline-steps`,
      );
      setSteps(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load pipeline");
    } finally {
      setIsLoading(false);
    }
  }, [jobTypeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const addStep = useCallback(
    async (stepType: string) => {
      if (!jobTypeId) return;
      await api.post(`/job-types/${jobTypeId}/pipeline-steps`, {
        stepType,
        label: formatStep(stepType),
        autoAdvanceCondition: null,
      });
      await load();
    },
    [jobTypeId, load],
  );

  const removeStep = useCallback(
    async (stepId: string) => {
      if (!jobTypeId) return;
      await api.delete(`/job-types/${jobTypeId}/pipeline-steps/${stepId}`);
      await load();
    },
    [jobTypeId, load],
  );

  const updateStep = useCallback(
    async (stepId: string, patch: Record<string, unknown>) => {
      if (!jobTypeId) return;
      await api.patch(
        `/job-types/${jobTypeId}/pipeline-steps/${stepId}`,
        patch,
      );
      await load();
    },
    [jobTypeId, load],
  );

  /**
   * Reorder by an ordered list of step ids via the dedicated two-phase
   * `PUT /reorder` endpoint, which assigns negative temp orders then the
   * final values in one transaction — dodging the @@unique([jobTypeId, order])
   * constraint. (The per-step PATCH route does NOT accept `order`: ajv
   * removeAdditional silently strips it, so order-bearing PATCHes are a no-op.)
   * On any failure the authoritative list is re-fetched.
   */
  const reorder = useCallback(
    async (orderedIds: string[]) => {
      if (!jobTypeId) return;
      try {
        await api.put(`/job-types/${jobTypeId}/pipeline-steps/reorder`, {
          steps: orderedIds.map((id, i) => ({ id, order: i })),
        });
      } finally {
        await load();
      }
    },
    [jobTypeId, load],
  );

  return {
    steps,
    isLoading,
    error,
    reload: load,
    addStep,
    removeStep,
    updateStep,
    reorder,
  };
}
